import { AlertCircle, Loader2, Trash2, Users } from "lucide-react";
import { useMemo, useState } from "react";

import { EmptyState, LoadingCards } from "components/states";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";

import { describeReadFailure } from "./describe-read-failure";
import { Notice } from "./Notice";
import type { BulkDeleteResult, PlatformAttendee } from "./schemas";
import { cn } from "utils";
import { searchMatcher } from "utils/search";
import { useGovernanceCommand } from "./use-governance-command";
import {
  usePlatformAttendees,
  PLATFORM_ATTENDEE_LIMIT,
} from "./use-platform-attendees";

// Mirrors MAX_TARGETS in attendee_bulk_deletion.py and the size bound in the
// BULK_DELETE_ATTENDEES rules arm. All three have to agree: the rules reject a
// larger batch outright, and the service fails the command.
export const MAX_BULK_DELETE = 50;

const SKIP_REASONS: Record<string, string> = {
  not_found: "Already gone",
  event_deleting: "Event is being deleted",
  event_admin: "Is an event administrator — demote them first",
  recreated: "Re-registered after this request was filed",
};

function targetKey(attendee: PlatformAttendee): string {
  return `${attendee.eventId}/${attendee.attendeeId}`;
}

function searchFields(attendee: PlatformAttendee) {
  return [
    attendee.name,
    attendee.email,
    attendee.company,
    attendee.role,
    attendee.eventId,
  ];
}

/**
 * Cross-event attendee maintenance.
 *
 * The reference platform's super-admin screen deletes by a flat array of
 * attendee ids. Connect cannot: an attendee id is sha256(email), so the same
 * person carries the *same* id in every event they attend, and an id on its own
 * names a person rather than a record. Every row here is therefore addressed by
 * the pair (eventId, attendeeId), and the command payload carries pairs.
 *
 * Deletion is not reversible by this screen. The cleanup trigger leaves an
 * `attendee_deletion_states` tombstone behind it, and every path that creates
 * an attendee refuses while that tombstone is not COMPLETED — registration,
 * CSV import, and ADD_EVENT_ATTENDEE, which all clear a COMPLETED one on
 * success. So the same address can come back by any of the three, an event
 * admin hand-adding them included.
 *
 * This comment used to say the tombstone was permanent and that only
 * self-registration or an import could clear it. That stopped being true when
 * attendee creation moved behind a command.
 */
export function AttendeesSection({ uid }: { uid: string }) {
  const attendees = usePlatformAttendees();
  const { state: cmdState, submit, reset } = useGovernanceCommand();
  const [term, setTerm] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);

  const busy =
    cmdState.status === "submitting" || cmdState.status === "watching";

  const visible = useMemo(() => {
    if (attendees.status !== "success") return [];
    const matches = searchMatcher(term);
    return attendees.data.filter((attendee) => matches(searchFields(attendee)));
  }, [attendees, term]);

  const chosen = useMemo(
    () =>
      attendees.status === "success"
        ? attendees.data.filter((attendee) => selected.has(targetKey(attendee)))
        : [],
    [attendees, selected],
  );

  const toggle = (attendee: PlatformAttendee) => {
    const key = targetKey(attendee);
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      // The rules refuse a larger batch, so stop at the bound rather than
      // letting the operator build a selection that cannot be submitted.
      else if (next.size < MAX_BULK_DELETE) next.add(key);
      return next;
    });
  };

  const confirmDelete = async () => {
    if (chosen.length === 0) return;
    setConfirming(false);
    await submit("BULK_DELETE_ATTENDEES", uid, {
      targets: chosen.map((attendee) => ({
        eventId: attendee.eventId,
        attendeeId: attendee.attendeeId,
      })),
    });
  };

  if (attendees.status === "loading") return <LoadingCards cards={4} />;
  if (attendees.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load attendees">
        {describeReadFailure(attendees.error)}
      </EmptyState>
    );

  // Terminal states own the whole section: the list behind them is stale the
  // moment the command succeeds, and reloading it under a result the operator
  // has not read yet would hide which rows were skipped.
  if (cmdState.status === "succeeded")
    return (
      <BulkDeleteOutcome
        result={cmdState.result}
        onDismiss={() => {
          reset();
          setSelected(new Set());
          attendees.reload();
        }}
      />
    );

  return (
    <div className="space-y-4" data-testid="attendees-content">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge variant="outline">
          {visible.length} of {attendees.data.length} attendees
        </Badge>
        {selected.size > 0 && (
          <Badge variant="outline" data-testid="attendee-selection-count">
            {selected.size} selected
            {selected.size >= MAX_BULK_DELETE && ` (max ${MAX_BULK_DELETE})`}
          </Badge>
        )}
      </div>

      {attendees.truncated && (
        <Notice tone="warning" testId="attendees-truncated">
          Showing the first {PLATFORM_ATTENDEE_LIMIT} attendee records across
          all events.
        </Notice>
      )}

      {cmdState.status === "failed" && (
        <Notice tone="error">{describeReadFailure(cmdState.error)}</Notice>
      )}

      <div className="flex gap-2">
        <Input
          type="search"
          placeholder="Search name, email, company or event"
          aria-label="Search attendees"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          disabled={busy}
          testId="attendee-search"
        />
        <Button
          variant="destructive"
          size="sm"
          disabled={busy || selected.size === 0}
          onClick={() => setConfirming(true)}
          data-testid="bulk-delete-attendees"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Delete {selected.size > 0 ? selected.size : ""}
        </Button>
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={Users} title="No attendees match">
          {attendees.data.length === 0
            ? "No events have attendees yet."
            : "Every attendee is filtered out by the current search."}
        </EmptyState>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card text-card-foreground">
          {visible.map((attendee) => {
            const key = targetKey(attendee);
            const isSelected = selected.has(key);
            return (
              <li
                key={key}
                className={cn(
                  "relative flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50",
                  isSelected && "bg-lit/10 hover:bg-lit/10",
                )}
              >
                {/* Below sm the label's ::after covers the row, so a thumb
                    on the name ticks the box, which alone is 20px. From sm
                    up only the box toggles and the email stays selectable. */}
                <label className="flex shrink-0 items-center max-sm:after:absolute max-sm:after:inset-0 max-sm:after:content-['']">
                  <input
                    type="checkbox"
                    className="accent-lit max-sm:size-5"
                    checked={isSelected}
                    disabled={
                      busy || (!isSelected && selected.size >= MAX_BULK_DELETE)
                    }
                    onChange={() => toggle(attendee)}
                    aria-label={`Select ${attendee.name ?? attendee.attendeeId} in ${attendee.eventId}`}
                    data-testid={`select-attendee-${key}`}
                  />
                </label>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">
                    {attendee.name || "(no name)"}
                  </p>
                  <p className="truncate text-sm text-muted-foreground">
                    {attendee.email || "No email on record"}
                    {attendee.company ? ` — ${attendee.company}` : ""}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-mono text-xs text-muted-foreground">
                    {attendee.eventId}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {attendee.status ?? "unknown"}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog
        open={confirming}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirming(false);
        }}
      >
        <DialogContent className="w-[min(32rem,calc(100vw-2rem))]">
          <DialogHeader>
            <DialogTitle>
              Delete {chosen.length} attendee
              {chosen.length === 1 ? "" : "s"}?
            </DialogTitle>
            <DialogDescription>
              This removes each attendee record and the event membership that
              points at it, so these people lose access to the events listed.
              Their agenda, speaker and directory entries are cleaned up
              afterwards. This cannot be undone here, and putting somebody back
              means adding them again on the event, which does not restore the
              sessions they presented or their track. Event administrators are
              skipped — demote them first.
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-48 space-y-1 overflow-y-auto text-sm text-muted-foreground">
            {chosen.map((attendee) => (
              <li key={targetKey(attendee)} className="truncate">
                {attendee.email || attendee.name || attendee.attendeeId}
                <span className="font-mono text-xs"> — {attendee.eventId}</span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={busy}
              data-testid="confirm-bulk-delete"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete {chosen.length} attendee
              {chosen.length === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * A bulk command succeeds with a report, not a tick.
 *
 * "Command succeeded" over a batch where half the targets were skipped is the
 * silent-partial-failure this screen has to avoid, so the count and every skip
 * reason are rendered, and the panel is styled as a warning whenever fewer
 * records were deleted than were asked for.
 */
function BulkDeleteOutcome({
  result,
  onDismiss,
}: {
  result: BulkDeleteResult | undefined;
  onDismiss: () => void;
}) {
  // An older command document, or a type that writes no report. Treat the
  // absence as "applied, details unknown" rather than inventing a count.
  if (!result)
    return (
      <div className="space-y-4" data-testid="bulk-delete-outcome">
        <EmptyState icon={Users} title="Attendees deleted">
          The command completed but reported no per-attendee detail.
        </EmptyState>
        <Button
          variant="secondary"
          size="sm"
          onClick={onDismiss}
          data-testid="dismiss-bulk-result"
        >
          Back to attendees
        </Button>
      </div>
    );

  const partial = result.deletedCount < result.requestedCount;
  return (
    <div className="space-y-4" data-testid="bulk-delete-outcome">
      <Notice
        tone={partial ? "warning" : "success"}
        testId="bulk-delete-summary"
      >
        Deleted {result.deletedCount} of {result.requestedCount} selected
        attendees.
      </Notice>

      {result.skipped.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-foreground">Skipped</p>
          <ul
            className="space-y-1 text-sm text-muted-foreground"
            data-testid="bulk-delete-skipped"
          >
            {result.skipped.map((skip) => (
              <li key={`${skip.eventId}/${skip.attendeeId}`}>
                <span className="font-mono text-xs">
                  {skip.eventId}/{skip.attendeeId.slice(0, 12)}…
                </span>{" "}
                — {SKIP_REASONS[skip.reason] ?? skip.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button
        variant="secondary"
        size="sm"
        onClick={onDismiss}
        data-testid="dismiss-bulk-result"
      >
        Back to attendees
      </Button>
    </div>
  );
}
